import { decodeHTML } from "entities"
import { Lexer, Marked, type Token, type Tokens } from "marked"
import { z } from "zod"
import { normalizeIframeUrl, parseBlockNoteContent } from "./editor-content"

const markdownParser = new Marked({
	extensions: [
		{
			name: "iframe",
			level: "block",
			start: (src) => src.match(/(?:^|\n) {0,3}<iframe(?=[\s/>])/i)?.index,
			tokenizer: (src) => {
				const opening =
					/^ {0,3}<iframe(?=[\s/>])((?:[^"'<>]|"[^"]*"|'[^']*')*)>/i.exec(src)
				if (!opening) {
					if (/^ {0,3}<iframe(?=[\s/>])/i.test(src))
						throw new Error("Invalid iframe opening tag.")
					return
				}
				const selfClosing = /\/\s*$/.test(opening[1]!)
				const closing = /^[\s\S]*?<\/iframe\s*>/i.exec(
					src.slice(opening[0].length)
				)
				if (!selfClosing && !closing)
					throw new Error("Iframe embeds must include a closing </iframe> tag.")
				const attributes = [
					...opening[1]!.matchAll(
						/([^\s"'=<>`/]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+)))?/g
					)
				]
				const source = attributes.find(
					(attribute) => attribute[1]!.toLowerCase() === "src"
				)
				const url = normalizeIframeUrl(
					decodeHTML(source?.[2] ?? source?.[3] ?? source?.[4] ?? "")
				)
				if (!url)
					throw new Error(
						"Iframe embeds require a valid HTTPS src URL without credentials."
					)
				return {
					type: "iframe",
					raw: opening[0] + (selfClosing ? "" : closing![0]),
					url
				}
			}
		}
	]
})

// A reader without the old editor runtime for saved documents from the previous editor.
// Keep this for history, backups and drafts created before the migration.
const legacyNode = z.object({
	type: z.string(),
	text: z.string().optional(),
	format: z.union([z.number(), z.string()]).optional(),
	style: z.string().optional(),
	tag: z.string().optional(),
	listType: z.string().optional(),
	start: z.number().optional(),
	checked: z.boolean().optional(),
	url: z.string().optional(),
	src: z.string().optional(),
	altText: z.string().optional(),
	language: z.string().optional(),
	get children(): z.ZodOptional<z.ZodArray<typeof legacyNode>> {
		return z.array(legacyNode).optional()
	}
})

function textContent(
	text: string,
	styles: Record<string, string | boolean> = {}
) {
	return { type: "text" as const, text, styles }
}

function makeBlock(
	type: string,
	content: unknown = [],
	props = {},
	children: unknown[] = []
) {
	return { id: crypto.randomUUID(), type, props, content, children }
}

function markdownInline(
	tokens: Token[],
	styles: Record<string, string | boolean> = {}
): unknown[] {
	return tokens.flatMap((token): unknown[] => {
		switch (token.type) {
			case "strong":
			case "em":
			case "del": {
				const style =
					token.type === "strong"
						? "bold"
						: token.type === "em"
							? "italic"
							: "strike"
				return markdownInline((token as Tokens.Strong).tokens, {
					...styles,
					[style]: true
				})
			}
			case "codespan":
				return [
					textContent((token as Tokens.Codespan).text, {
						...styles,
						code: true
					})
				]
			case "link": {
				const link = token as Tokens.Link
				return [
					{
						type: "link",
						href: decodeHTML(link.href),
						content: markdownInline(link.tokens, styles)
					}
				]
			}
			case "br":
				return [textContent("\n", styles)]
			case "checkbox":
				return []
			case "image":
				return [textContent((token as Tokens.Image).text, styles)]
			default:
				return "tokens" in token && token.tokens
					? markdownInline(token.tokens, styles)
					: [
							textContent(
								decodeHTML("text" in token ? token.text : token.raw),
								styles
							)
						]
		}
	})
}

function markdownBlocks(tokens: Token[]): ReturnType<typeof makeBlock>[] {
	return tokens.flatMap((token): ReturnType<typeof makeBlock>[] => {
		switch (token.type) {
			case "space":
				return []
			case "hr":
				return [makeBlock("divider")]
			case "iframe":
				return [
					{ ...makeBlock("iframe", [], { url: token.url }), content: undefined }
				]
			case "code": {
				const code = token as Tokens.Code
				return [
					makeBlock("codeBlock", [textContent(code.text)], {
						language: code.lang?.split(" ")[0] ?? "text"
					})
				]
			}
			case "heading": {
				const heading = token as Tokens.Heading
				return [
					makeBlock("heading", markdownInline(heading.tokens), {
						level: heading.depth
					})
				]
			}
			case "blockquote":
				return markdownBlocks((token as Tokens.Blockquote).tokens).map(
					(block) =>
						block.type === "paragraph"
							? { ...block, type: "quote" }
							: makeBlock("quote", [], {}, [block])
				)
			case "list": {
				const list = token as Tokens.List
				return list.items.map((item, index) => {
					const [first, ...children] = markdownBlocks(item.tokens)
					return makeBlock(
						item.task
							? "checkListItem"
							: list.ordered
								? "numberedListItem"
								: "bulletListItem",
						first?.type === "paragraph" ? first.content : [],
						item.task
							? { checked: !!item.checked }
							: list.ordered
								? { start: Number(list.start) + index }
								: {},
						first?.type === "paragraph"
							? [...first.children, ...children]
							: first
								? [first, ...children]
								: children
					)
				})
			}
			case "table": {
				const table = token as Tokens.Table
				return [
					{
						...makeBlock("table"),
						content: {
							type: "tableContent",
							columnWidths: table.header.map(() => null),
							headerRows: 1,
							rows: [table.header, ...table.rows].map((row) => ({
								cells: row.map((cell) => markdownInline(cell.tokens))
							}))
						}
					}
				]
			}
			default: {
				const inline =
					"tokens" in token && token.tokens
						? token.tokens
						: Lexer.lexInline("text" in token ? token.text : token.raw)
				// Images are blocks in BlockNote; split paragraphs at image boundaries.
				const blocks: ReturnType<typeof makeBlock>[] = []
				let pending: Token[] = []
				for (const part of inline) {
					if (part.type === "image") {
						if (pending.length)
							blocks.push(makeBlock("paragraph", markdownInline(pending)))
						pending = []
						const image = part as Tokens.Image
						blocks.push(
							makeBlock("image", [], {
								url: image.href,
								name: image.text,
								caption: image.title ?? ""
							})
						)
					} else pending.push(part)
				}
				if (pending.length)
					blocks.push(makeBlock("paragraph", markdownInline(pending)))
				return blocks
			}
		}
	})
}

function legacyInline(nodes: z.infer<typeof legacyNode>[]): unknown[] {
	return nodes.flatMap((node): unknown[] => {
		if (node.type === "link" || node.type === "autolink")
			return [
				{
					type: "link",
					href: node.url ?? "",
					content: legacyInline(node.children ?? [])
				}
			]
		if (node.type === "linebreak") return [textContent("\n")]
		if (node.type === "tab") return [textContent("\t")]
		if (node.type !== "text" && node.type !== "code-highlight")
			throw new Error(`Unsupported legacy inline node: ${node.type}`)
		const format = typeof node.format === "number" ? node.format : 0
		const styles: Record<string, string | boolean> = {}
		for (const [key, bit] of Object.entries({
			bold: 1,
			italic: 2,
			strike: 4,
			underline: 8,
			code: 16
		}))
			if (format & bit) styles[key] = true
		if (format & (32 | 64))
			throw new Error("Legacy subscript/superscript requires manual conversion")
		for (const declaration of (node.style ?? "").split(";")) {
			const [key, ...value] = declaration.split(":")
			if ((key ?? "").trim() === "color")
				styles.textColor = value.join(":").trim()
			if ((key ?? "").trim() === "background-color")
				styles.backgroundColor = value.join(":").trim()
		}
		return [textContent(node.text ?? "", styles)]
	})
}

// Legacy heading tags may be out of range in pre-migration documents.
// Clamp 1-6 to bound repeat/serialization output; non-numeric or negative
// levels cannot render and stop conversion instead.
function legacyHeadingLevel(node: z.infer<typeof legacyNode>) {
	const level = Number(node.tag?.slice(1) ?? 1)
	if (!Number.isInteger(level) || level < 0)
		throw new Error(`Unsupported legacy heading level: ${node.tag}`)
	return Math.min(Math.max(level, 1), 6)
}

function legacyBlocks(
	nodes: z.infer<typeof legacyNode>[]
): ReturnType<typeof makeBlock>[] {
	return nodes.flatMap((node): ReturnType<typeof makeBlock>[] => {
		const children = node.children ?? []
		if (node.type === "list")
			return children.map((item, index) => {
				const nested =
					item.children?.filter((child) => child.type === "list") ?? []
				return makeBlock(
					node.listType === "check"
						? "checkListItem"
						: node.listType === "number"
							? "numberedListItem"
							: "bulletListItem",
					legacyInline(
						item.children?.filter((child) => child.type !== "list") ?? []
					),
					node.listType === "check"
						? { checked: !!item.checked }
						: node.listType === "number"
							? { start: (node.start ?? 1) + index }
							: {},
					legacyBlocks(nested)
				)
			})
		if (node.type === "horizontalrule") return [makeBlock("divider")]
		if (node.type === "image")
			return [
				makeBlock("image", [], {
					url: node.src ?? "",
					name: node.altText ?? "",
					caption: node.altText ?? ""
				})
			]
		if (!["paragraph", "heading", "quote", "code"].includes(node.type))
			throw new Error(`Unsupported legacy block: ${node.type}`)
		// Saved paragraphs can wrap paragraphs or images; preserve their block boundaries.
		const blocks: ReturnType<typeof makeBlock>[] = []
		let inline: z.infer<typeof legacyNode>[] = []
		const props =
			node.type === "heading"
				? { level: legacyHeadingLevel(node) }
				: node.type === "code"
					? { language: node.language ?? "text" }
					: {}
		const blockType = node.type === "code" ? "codeBlock" : node.type
		for (const child of children) {
			if (child.type === "image" || child.type === "paragraph") {
				if (inline.length)
					blocks.push(makeBlock(blockType, legacyInline(inline), props))
				inline = []
				blocks.push(...legacyBlocks([child]))
			} else inline.push(child)
		}
		if (inline.length || !blocks.length)
			blocks.push(makeBlock(blockType, legacyInline(inline), props))
		if (
			typeof node.format === "string" &&
			["left", "right", "center", "justify"].includes(node.format)
		)
			for (const block of blocks)
				block.props = { ...block.props, textAlignment: node.format }
		return blocks
	})
}

function legacyMarkdown(
	nodes: z.infer<typeof legacyNode>[],
	depth = 0
): string {
	return nodes
		.map((node) => {
			// Render children at most once per node: nested lists used to be visited
			// both by an eager content render and again inside the list case,
			// doubling conversion work per list level.
			const children = node.children ?? []
			switch (node.type) {
				case "text":
				case "code-highlight": {
					let text = node.text ?? ""
					const format = typeof node.format === "number" ? node.format : 0
					if (format & 16) return "`" + text + "`"
					text = text.replace(/([\\`*_[\]])/g, "\\$1")
					if (format & 1) text = `**${text}**`
					if (format & 2) text = `*${text}*`
					if (format & 4) text = `~~${text}~~`
					return text
				}
				case "linebreak":
					return "\n"
				case "tab":
					return "\t"
				case "link":
				case "autolink":
					return `[${legacyMarkdown(children, depth)}](${node.url ?? ""})`
				case "image":
					return `![${node.altText ?? ""}](${node.src ?? ""})`
				case "heading":
					return `${"#".repeat(legacyHeadingLevel(node))} ${legacyMarkdown(children, depth)}\n\n`
				case "quote":
					return (
						legacyMarkdown(children, depth)
							.split("\n")
							.map((line) => `> ${line}`)
							.join("\n") + "\n\n"
					)
				case "code":
					return `\`\`\`${node.language ?? ""}\n${children.map((child) => (child.type === "linebreak" ? "\n" : child.type === "tab" ? "\t" : (child.text ?? ""))).join("")}\n\`\`\`\n\n`
				case "horizontalrule":
					return "---\n\n"
				case "list":
					return (
						children
							.map((item, index) => {
								const itemChildren = item.children ?? []
								const text = legacyMarkdown(
									itemChildren.filter((child) => child.type !== "list"),
									depth + 1
								).trimEnd()
								const nested = legacyMarkdown(
									itemChildren.filter((child) => child.type === "list"),
									depth + 1
								)
								const marker =
									node.listType === "number"
										? `${(node.start ?? 1) + index}.`
										: "-"
								const checkbox =
									node.listType === "check"
										? `[${item.checked ? "x" : " "}] `
										: ""
								return `${"  ".repeat(depth)}${marker} ${checkbox}${text}\n${nested}`
							})
							.join("") + "\n"
					)

				case "paragraph":
					return legacyMarkdown(children, depth) + "\n\n"
				default:
					return legacyMarkdown(children, depth)
			}
		})
		.join("")
}

export function markdownToEditorContent(markdown: string | null) {
	const blocks = markdownBlocks(markdownParser.lexer(markdown ?? ""))
	return JSON.stringify({
		format: "blocknote",
		version: 1,
		blocks: blocks.length ? blocks : [makeBlock("paragraph")],
		markdown: markdown ?? ""
	})
}

export function textToEditorContent(text: string | null) {
	return JSON.stringify({
		format: "blocknote",
		version: 1,
		blocks: (text ?? "")
			.split("\n")
			.map((line) => makeBlock("paragraph", line ? [textContent(line)] : [])),
		markdown: (text ?? "").replace(/([\\`*_[\]#>~])/g, "\\$1")
	})
}

export function normalizeEditorContent(value: string | null): string {
	if (!value) return textToEditorContent(null)
	const blockNote = parseBlockNoteContent(value)
	if (blockNote) return JSON.stringify(blockNote)
	let parsed: unknown
	try {
		parsed = JSON.parse(value)
	} catch {
		return markdownToEditorContent(value)
	}
	const legacy = z.object({ root: legacyNode }).safeParse(parsed)
	if (!legacy.success) {
		if (
			typeof parsed === "object" &&
			parsed !== null &&
			("root" in parsed || "format" in parsed)
		)
			throw new Error("Invalid editor document")
		return markdownToEditorContent(value)
	}
	const roots = legacy.data.root.children ?? []
	// Bound legacy trees before conversion: depth and node count budget the
	// synchronous legacyBlocks/legacyMarkdown work.
	const pending: { nodes: z.infer<typeof legacyNode>[]; depth: number }[] = [
		{ nodes: roots, depth: 0 }
	]
	let counted = 0
	while (pending.length) {
		const level = pending.pop()!
		for (const node of level.nodes) {
			if (++counted > 20_000)
				throw new Error("Editor document exceeds the maximum node count")
			const nodeChildren = node.children ?? []
			if (nodeChildren.length) {
				if (level.depth >= 100)
					throw new Error("Editor document exceeds the maximum nesting depth")
				pending.push({ nodes: nodeChildren, depth: level.depth + 1 })
			}
		}
	}
	const blocks = legacyBlocks(roots)
	const result = JSON.stringify({
		format: "blocknote",
		version: 1,
		blocks: blocks.length ? blocks : [makeBlock("paragraph")],
		markdown: legacyMarkdown(roots).trimEnd()
	})
	if (!parseBlockNoteContent(result))
		throw new Error("Legacy document conversion failed validation")
	return result
}
